import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Parse from '../../parseConfig';
import MapShell from '../MapShell';
import { fetchMapLayers } from '../helpers/fetchMapLayers';
import useUIPresentationMode from '../helpers/useUIPresentationMode';
import StockMapComponent from '../stock/StockMapComponent';
import { replaceUrlState } from '../helpers/urlUpdaterHelper';
import { userHasBuiltHomeAccessRole } from './builtHomeAccess';
import {
  BUILT_HOME_MAP_REVIEW_FILTERS,
  BUILT_HOME_TYPE_FILTERS,
  BUILD_STYLE_FILTERS,
  getBuiltHomeAssignmentFilter,
  getBuiltHomeReviewFilter,
} from './builtProjectUtils';
import { fetchBuiltData } from './fetchBuiltData';
import { fetchBasePlans } from './fetchBasePlans';
import { getBasePlanFilterOptions, getBasePlanFilterValue } from './basePlanSelection';
import {
  getAssignmentFilterOptions,
  getConsultantFilterOptions,
  getConsultantFilterValue,
} from './builtFilterOptions';
import {
  areBuiltFilterSelectionsEqual,
  BUILT_FILTER_GROUPS,
  createAdaptiveBuiltFilterSelection,
  getSelectedBuiltFilterValues,
  isBuiltFilterValueSelected,
  readBuiltFilterUrlState,
  reconcileBuiltFilterSelection,
  writeBuiltFilterUrlState,
} from './builtFilterUrlState';
import BuiltSideBarLeft from './BuiltSideBarLeft';
import BuiltSideBarRight from './BuiltSideBarRight';
import BuiltHomeAdminModal from './BuiltHomeAdminModal';
import BuiltHomeReviewModal from './BuiltHomeReviewModal';
import BuiltHomeImportModal from './BuiltHomeImportModal';
import BuiltHomePdfLightbox from './BuiltHomePdfLightbox';
import BuiltHomePhotoLightbox from './BuiltHomePhotoLightbox';

const isAgentImportRequested = () => (
  new URLSearchParams(window.location.search).get('builtImport') === '1'
);

export default function BuiltLayout({ onLogout = () => {} }) {
  const { presentationModeSize, presentationModeiFrame } = useUIPresentationMode();
  const currentUser = Parse.User.current();
  const mapRef = useRef(null);
  const openRightSidebarRef = useRef(null);
  const closeRightSidebarRef = useRef(null);
  const pendingZoomAfterClearRef = useRef(false);

  const [selectedHome, setSelectedHome] = useState(null);
  const [homes, setHomes] = useState([]);
  const [basePlans, setBasePlans] = useState([]);
  const [isLoadingBasePlans, setIsLoadingBasePlans] = useState(false);
  const [basePlanLoadError, setBasePlanLoadError] = useState('');
  const [hasLoadedHomes, setHasLoadedHomes] = useState(false);
  const [filterUrlState, setFilterUrlState] = useState(() => (
    readBuiltFilterUrlState(window.location.search)
  ));
  const [layers, setLayers] = useState({ primary: [], secondary: [] });
  const [selectedBaseMap, setSelectedBaseMap] = useState('None');
  const [isParcelBoundariesVisible, setIsParcelBoundariesVisible] = useState(true);
  const [canManageHomes, setCanManageHomes] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(isAgentImportRequested);
  const [editHome, setEditHome] = useState(null);
  const [pdfHome, setPdfHome] = useState(null);
  const [photoHome, setPhotoHome] = useState(null);
  const isBasePlanPickerOpen = isAdminOpen || isReviewOpen || Boolean(editHome);

  const applyHomes = useCallback((data) => {
    setHomes(data);
    setHasLoadedHomes(true);
  }, []);

  const refreshHomes = useCallback(async () => {
    try {
      applyHomes(await fetchBuiltData());
    } catch (error) {
      console.error('Failed to refresh built homes:', error);
    }
  }, [applyHomes]);

  const closeImport = useCallback(() => {
    setIsImportOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('builtImport');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const checkEditorRole = useCallback(async () => {
    if (!currentUser) {
      setCanManageHomes(false);
      return;
    }

    try {
      setCanManageHomes(await userHasBuiltHomeAccessRole(currentUser));
    } catch (error) {
      console.error('Failed to check Built Home role membership:', error);
      setCanManageHomes(false);
    }
  }, [currentUser]);

  useEffect(() => {
    let isCancelled = false;

    fetchBuiltData()
      .then((data) => {
        if (!isCancelled) applyHomes(data);
      })
      .catch((error) => {
        console.error('Failed to load built homes:', error);
      });

    return () => { isCancelled = true; };
  }, [applyHomes]);

  useEffect(() => {
    if (!isBasePlanPickerOpen) return undefined;

    let isCancelled = false;
    setIsLoadingBasePlans(true);
    setBasePlanLoadError('');

    fetchBasePlans()
      .then((plans) => {
        if (!isCancelled) setBasePlans(plans);
      })
      .catch((error) => {
        console.error('Failed to load base plans:', error);
        if (!isCancelled) {
          setBasePlans([]);
          setBasePlanLoadError('Base plans could not be loaded.');
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingBasePlans(false);
      });

    return () => { isCancelled = true; };
  }, [isBasePlanPickerOpen]);

  useEffect(() => {
    checkEditorRole();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkEditorRole();
    };
    window.addEventListener('focus', checkEditorRole);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', checkEditorRole);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkEditorRole]);

  useEffect(() => {
    let isCancelled = false;

    fetchMapLayers().then((nextLayers) => {
      if (isCancelled) return;

      setLayers(nextLayers);

      const url = new URL(window.location.href);
      const baseMapIndex = Number.parseInt(url.searchParams.get('bm'), 10);
      const secondaryLayers = Array.isArray(nextLayers.secondary) ? nextLayers.secondary : [];
      const initialBaseMap = baseMapIndex === -1
        ? 'None'
        : secondaryLayers[baseMapIndex]?.get('name') || secondaryLayers[0]?.get('name') || 'None';
      const showParcelBoundaries = url.searchParams.has('pb')
        ? url.searchParams.get('pb') === '1'
        : true;

      setSelectedBaseMap(initialBaseMap);
      setIsParcelBoundariesVisible(showParcelBoundaries);

      if (!url.searchParams.has('pb')) {
        url.searchParams.set('pb', '1');
        window.history.replaceState({}, '', url);
      }
    });

    return () => { isCancelled = true; };
  }, []);

  const changeBaseMap = useCallback((name) => {
    setSelectedBaseMap(name);
    const url = new URL(window.location.href);
    const layerIndex = layers.secondary.findIndex((layer) => layer.get('name') === name);
    url.searchParams.set('bm', name === 'None' ? '-1' : String(layerIndex));
    window.history.replaceState({}, '', url);
  }, [layers.secondary]);

  const toggleParcelBoundaries = useCallback((visible) => {
    setIsParcelBoundariesVisible(visible);
    const url = new URL(window.location.href);
    url.searchParams.set('pb', visible ? '1' : '0');
    window.history.replaceState({}, '', url);
  }, []);

  const buildStyleOptions = useMemo(
    () => getAssignmentFilterOptions(homes, 'buildStyle', BUILD_STYLE_FILTERS),
    [homes]
  );
  const typeOptions = useMemo(
    () => getAssignmentFilterOptions(homes, 'type', BUILT_HOME_TYPE_FILTERS),
    [homes]
  );
  const consultantOptions = useMemo(() => getConsultantFilterOptions(homes), [homes]);
  const basePlanFilterOptions = useMemo(() => getBasePlanFilterOptions(homes), [homes]);
  const hasNeedsReview = useMemo(
    () => homes.some((home) => home.needsReview === 1),
    [homes]
  );
  const buildStyleOptionValues = useMemo(() => buildStyleOptions, [buildStyleOptions]);
  const typeOptionValues = useMemo(() => typeOptions, [typeOptions]);
  const reviewOptionValues = useMemo(
    () => (hasNeedsReview ? BUILT_HOME_MAP_REVIEW_FILTERS.map(({ value }) => value) : []),
    [hasNeedsReview]
  );
  const basePlanOptionValues = useMemo(
    () => basePlanFilterOptions.map(({ value }) => value),
    [basePlanFilterOptions]
  );
  const consultantOptionValues = useMemo(
    () => consultantOptions.map(({ value }) => value),
    [consultantOptions]
  );

  const filters = useMemo(
    () => getSelectedBuiltFilterValues(filterUrlState[BUILT_FILTER_GROUPS.BUILD_STYLE], buildStyleOptionValues),
    [buildStyleOptionValues, filterUrlState]
  );
  const typeFilters = useMemo(
    () => getSelectedBuiltFilterValues(filterUrlState[BUILT_FILTER_GROUPS.TYPE], typeOptionValues),
    [filterUrlState, typeOptionValues]
  );
  const reviewFilters = useMemo(
    () => getSelectedBuiltFilterValues(filterUrlState[BUILT_FILTER_GROUPS.REVIEW], reviewOptionValues),
    [filterUrlState, reviewOptionValues]
  );
  const basePlanFilters = useMemo(() => {
    const selection = filterUrlState[BUILT_FILTER_GROUPS.BASE_PLAN];
    return selection.mode === 'all'
      ? null
      : getSelectedBuiltFilterValues(selection, basePlanOptionValues);
  }, [basePlanOptionValues, filterUrlState]);
  const consultantFilters = useMemo(() => {
    const selection = filterUrlState[BUILT_FILTER_GROUPS.CONSULTANT];
    return selection.mode === 'all'
      ? null
      : getSelectedBuiltFilterValues(selection, consultantOptionValues);
  }, [consultantOptionValues, filterUrlState]);

  const updateFilterGroup = useCallback((group, selectedValues, optionValues) => {
    setFilterUrlState((current) => ({
      ...current,
      [group]: createAdaptiveBuiltFilterSelection(selectedValues, optionValues),
    }));
  }, []);

  const setFilters = useCallback((selectedValues) => {
    updateFilterGroup(BUILT_FILTER_GROUPS.BUILD_STYLE, selectedValues, buildStyleOptionValues);
  }, [buildStyleOptionValues, updateFilterGroup]);
  const setTypeFilters = useCallback((selectedValues) => {
    updateFilterGroup(BUILT_FILTER_GROUPS.TYPE, selectedValues, typeOptionValues);
  }, [typeOptionValues, updateFilterGroup]);
  const setReviewFilters = useCallback((selectedValues) => {
    updateFilterGroup(BUILT_FILTER_GROUPS.REVIEW, selectedValues, reviewOptionValues);
  }, [reviewOptionValues, updateFilterGroup]);
  const setBasePlanFilters = useCallback((selectedValues) => {
    updateFilterGroup(
      BUILT_FILTER_GROUPS.BASE_PLAN,
      selectedValues === null ? new Set(basePlanOptionValues) : selectedValues,
      basePlanOptionValues
    );
  }, [basePlanOptionValues, updateFilterGroup]);
  const setConsultantFilters = useCallback((selectedValues) => {
    updateFilterGroup(
      BUILT_FILTER_GROUPS.CONSULTANT,
      selectedValues === null ? new Set(consultantOptionValues) : selectedValues,
      consultantOptionValues
    );
  }, [consultantOptionValues, updateFilterGroup]);

  useEffect(() => {
    if (!hasLoadedHomes) return;
    const optionValuesByGroup = {
      [BUILT_FILTER_GROUPS.BUILD_STYLE]: buildStyleOptionValues,
      [BUILT_FILTER_GROUPS.TYPE]: typeOptionValues,
      [BUILT_FILTER_GROUPS.REVIEW]: reviewOptionValues,
      [BUILT_FILTER_GROUPS.BASE_PLAN]: basePlanOptionValues,
      [BUILT_FILTER_GROUPS.CONSULTANT]: consultantOptionValues,
    };

    setFilterUrlState((current) => {
      const next = { ...current };
      let changed = false;
      Object.entries(optionValuesByGroup).forEach(([group, optionValues]) => {
        const reconciled = reconcileBuiltFilterSelection(current[group], optionValues);
        if (!areBuiltFilterSelectionsEqual(current[group], reconciled)) {
          next[group] = reconciled;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [
    basePlanOptionValues,
    buildStyleOptionValues,
    consultantOptionValues,
    hasLoadedHomes,
    reviewOptionValues,
    typeOptionValues,
  ]);

  useEffect(() => {
    const nextUrl = writeBuiltFilterUrlState(new URL(window.location.href), filterUrlState);
    if (nextUrl.href !== window.location.href) replaceUrlState(nextUrl);
  }, [filterUrlState]);

  useEffect(() => {
    const handlePopState = () => setFilterUrlState(readBuiltFilterUrlState(window.location.search));
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const filteredHomes = useMemo(() => {
    return homes.filter((home) => (
      isBuiltFilterValueSelected(
        filterUrlState[BUILT_FILTER_GROUPS.BUILD_STYLE],
        getBuiltHomeAssignmentFilter(home.buildStyle)
      )
      && isBuiltFilterValueSelected(
        filterUrlState[BUILT_FILTER_GROUPS.TYPE],
        getBuiltHomeAssignmentFilter(home.type)
      )
      && (!hasNeedsReview || isBuiltFilterValueSelected(
        filterUrlState[BUILT_FILTER_GROUPS.REVIEW],
        getBuiltHomeReviewFilter(home)
      ))
      && isBuiltFilterValueSelected(
        filterUrlState[BUILT_FILTER_GROUPS.BASE_PLAN],
        getBasePlanFilterValue(home)
      )
      && isBuiltFilterValueSelected(
        filterUrlState[BUILT_FILTER_GROUPS.CONSULTANT],
        getConsultantFilterValue(home)
      )
    ));
  }, [filterUrlState, hasNeedsReview, homes]);

  useEffect(() => {
    if (pendingZoomAfterClearRef.current) {
      requestAnimationFrame(() => {
        mapRef.current?.zoomToAll?.();
        pendingZoomAfterClearRef.current = false;
      });
    }
  }, [filteredHomes]);

  useEffect(() => {
    if (!selectedHome) return;
    const nextSelectedHome = filteredHomes.find((home) => home.id === selectedHome.id);
    if (nextSelectedHome) {
      if (nextSelectedHome !== selectedHome) setSelectedHome(nextSelectedHome);
    } else {
      setSelectedHome(null);
      mapRef.current?.clearMarkerSelection?.();
    }
  }, [filteredHomes, selectedHome]);

  useEffect(() => {
    if (!selectedHome) closeRightSidebarRef.current?.();
  }, [selectedHome]);

  return (
    <>
      <MapShell
        presentationModeSize={presentationModeSize}
        defaultRightOpen={false}
        desktopRightSidebarDefaultOpen={false}
        rightSidebarAvailable={Boolean(selectedHome)}
        onRequestRightSidebarOpen={(openSidebar) => { openRightSidebarRef.current = openSidebar; }}
        onRequestRightSidebarClose={(closeSidebar) => { closeRightSidebarRef.current = closeSidebar; }}
        leftContent={
          <BuiltSideBarLeft
            isVisible={presentationModeSize > 1}
            showAdminButton={canManageHomes}
            onOpenAdmin={() => setIsAdminOpen(true)}
            onLogout={onLogout}
            filters={filters}
            setFilters={setFilters}
            buildStyleOptions={buildStyleOptions}
            typeFilters={typeFilters}
            setTypeFilters={setTypeFilters}
            typeOptions={typeOptions}
            basePlanFilters={basePlanFilters}
            setBasePlanFilters={setBasePlanFilters}
            basePlanOptions={basePlanFilterOptions}
            reviewFilters={reviewFilters}
            setReviewFilters={setReviewFilters}
            hasNeedsReview={hasNeedsReview}
            consultantFilters={consultantFilters}
            setConsultantFilters={setConsultantFilters}
            consultantOptions={consultantOptions}
            onClearAndZoom={() => { pendingZoomAfterClearRef.current = true; }}
            layers={layers}
            selectedBaseMap={selectedBaseMap}
            setSelectedBaseMap={changeBaseMap}
            isParcelBoundariesVisible={isParcelBoundariesVisible}
            toggleParcelBoundaries={toggleParcelBoundaries}
          />
        }
        mapContent={
          <StockMapComponent
            ref={mapRef}
            presentationModeiFrame={presentationModeiFrame}
            setSelectedListing={setSelectedHome}
            listings={filteredHomes}
            listingCount={filteredHomes.length}
            onForceRightSidebarOpen={() => openRightSidebarRef.current?.()}
            enableLayerPicker
            layers={layers}
            selectedBaseMap={selectedBaseMap}
            isParcelBoundariesVisible={isParcelBoundariesVisible}
            clusterStyle="usage"
            clusterClickPadding={80}
            viewportClustering
            minimumZoom={null}
            satelliteMapTypeId="hybrid"
            persistViewportInUrl
          />
        }
        rightContent={
          <BuiltSideBarRight
            selectedHome={selectedHome}
            showEditButton={canManageHomes}
            onEditHome={() => {
              if (!selectedHome) return;
              setEditHome(selectedHome);
            }}
            onOpenPhoto={() => {
              if (selectedHome?.photo) setPhotoHome(selectedHome);
            }}
            onOpenPlansPdf={() => {
              if (selectedHome?.plansPdf) setPdfHome(selectedHome);
            }}
            clearSelection={() => {
              setSelectedHome(null);
              mapRef.current?.clearMarkerSelection?.();
              closeRightSidebarRef.current?.();
            }}
          />
        }
      />

      <BuiltHomeAdminModal
        isOpen={isAdminOpen}
        homes={homes}
        basePlans={basePlans}
        isLoadingBasePlans={isLoadingBasePlans}
        basePlanLoadError={basePlanLoadError}
        onEditHome={(home) => {
          setEditHome(home);
        }}
        onLocateHome={(home) => {
          mapRef.current?.focusListing?.(home);
        }}
        onClose={() => setIsAdminOpen(false)}
        onHomesChanged={refreshHomes}
        onOpenReview={() => {
          setIsAdminOpen(false);
          setEditHome(null);
          setIsReviewOpen(true);
        }}
      />

      <BuiltHomeReviewModal
        isOpen={isReviewOpen || Boolean(editHome)}
        homes={homes}
        basePlans={basePlans}
        isLoadingBasePlans={isLoadingBasePlans}
        basePlanLoadError={basePlanLoadError}
        mode={editHome ? 'edit' : 'review'}
        home={editHome}
        onClose={() => {
          setIsReviewOpen(false);
          setEditHome(null);
        }}
        onHomesChanged={refreshHomes}
      />

      <BuiltHomeImportModal
        isOpen={isImportOpen}
        onClose={closeImport}
        onHomesChanged={refreshHomes}
      />

      <BuiltHomePdfLightbox
        home={pdfHome}
        onClose={() => setPdfHome(null)}
      />

      <BuiltHomePhotoLightbox
        home={photoHome}
        onClose={() => setPhotoHome(null)}
      />
    </>
  );
}
